import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import update from 'update-immutable';
import { camelCase, isEmpty, isNil } from 'lodash';
import { isMobile } from 'react-device-detect';
import MoveTo from 'moveto';
import cc from 'classcat';
import loadable from '@loadable/component';
import { affiliateGet, affiliateRemove, affiliateSet } from '@lindar-joy/mrq-affiliate';
import { Helmet } from 'react-helmet-async';
import {
  passwordErrorMessage,
  country,
  phoneNumberErrorMessage,
  postcodeErrorMessage
} from 'lib/messages';
import Api from 'services/Api';
import withLazyStyle from 'components/LazyStyle';
import { addBeforeUnloadListener, removeBeforeUnloadListener } from 'lib/addBeforeUnloadListener';
import { isLoading } from 'lib/redux-utils';
import localStore, { GLOBAL } from 'lib/localStore';
import { getReturningPlayer } from 'lib/returningPlayer';
import { getExperiment, getExperimentData, experiments } from 'lib/analytics';
import UserBox from 'components/UserBox';
import { MagicMove } from 'components/Transitions';
import Auth from 'modules/Auth';
import Banner from 'modules/Banner';
import Modals from 'modules/Modals';
import { actions as routerActions } from 'modules/Router';
import Analytics from 'modules/Analytics';
import {
  birthdayOver18,
  phoneCode,
  promotionCode as validatePromotionCode,
  addressLine2 as validateAddressLine2,
  phoneWithPrefix as validatePhone
} from 'lib/valFuncs';
import {
  validateRequired,
  emailIsAvailable as validateEmail,
  password as validatePassword
} from 'lib/valFunctors';
import {
  address as addressRegex,
  city as cityRegex,
  name as firstOrLastNameValidation,
  postcode as postcodeRegex
} from 'lib/valRegexes';
import { numbersOnly } from 'lib/formatters';
import { phonePrefix } from 'lib/constants';
import Announcements from 'components/Notifications/Announcements';
import AccessDenied from 'components/AccessDenied';
import ProgressStage from 'components/ProgressStage';
import RegistrationOfferPromoBanner from 'components/RegistrationOfferPromoBanner';
import { trackMarketingPreferences } from 'components/MarketingPreferences';
import SignupVerticalBanner from './SignupVerticalBanner';
import PersonalInfo from './PersonalInfo';
import RegisterTrustFactors from './RegisterTrustFactors';
import AccountDetails from './AccountDetails';
import { meta } from './data';
import registrationMiddlestyle from './registrationMiddle.css?lazy';
import './signup.css';

const ConfirmationOld = loadable(() =>
  import(/* webpackChunkName: "Confirmation" */ './Confirmation')
);

const Confirmation = loadable(() =>
  import(/* webpackChunkName: "Confirmation" */ './components/Confirmation')
);

const { push, replace, stripSearch } = routerActions;
const globalStore = localStore.instances.get(GLOBAL);
const signupSteps = ['account-details', 'personal-info', 'confirmation'];
const isRedirectReturningPlayersEnabled = getExperiment(
  experiments.redirectReturningPlayers.flag
)?.key;
const shouldShowSignupPromoCode = !getExperiment(experiments.hideSignupPromoCodeField.flag)?.key;
const marketingPreferencesExperiment = getExperimentData(
  experiments.signupMarketingPreferencesRedesign.flag
);
const shouldShowMarketingPreferencesRedesign = marketingPreferencesExperiment.isTreatment;
const manualAddressFieldKeys = ['address', 'address2', 'postal', 'city'];
const easeFunctions = {
  easeOutQuad: (t, b, c, d) => {
    const newCurrentTime = t / d;
    return -c * newCurrentTime * (newCurrentTime - 2) + b;
  }
};
const mover = new MoveTo(
  {
    duration: 500,
    easing: 'easeOutQuad'
  },
  easeFunctions
);
const scrollTolerance = 30;

const serverKeyMap = {
  line1: 'address',
  line2: 'address2',
  affiliate: 'affiliate',
  birthDate: 'birthday',
  city: 'city',
  country: 'country',
  email: 'email',
  firstName: 'firstName',
  title: 'title',
  lastName: 'lastName',
  phoneNumber: 'phoneNumber',
  postcode: 'postal',
  promotionCode: 'promotionCode',
  referrer: 'by',
  referralRef: 'ref',
  partialUserRef: 'partialUserRef',
  password: 'password',
  receiveCalls: 'agreeMarketing',
  receiveEmail: 'agreeMarketing',
  receivePost: 'agreeMarketing',
  receiveSms: 'agreeMarketing',
  username: 'username'
};

// TODO: move to lib (?)
const toErrorMap = (acc, val) => {
  // eslint-disable-next-line no-param-reassign -- reduce accumulator
  acc[serverKeyMap[val.field]] = val.msg;
  return acc;
};

const updateInitials = (fields, initialData) => {
  Object.keys(initialData).forEach((key) => {
    if (fields[key] && fields[key].initial !== initialData[key]) {
      // eslint-disable-next-line no-param-reassign -- expected forEach mutation
      fields[key].initial = initialData[key];
    }
  });
};

/**
 * Replaces invalid space characters in PersonalInfo
 * with a valid regular space character
 */
const sanitizePersonalInfoData = (data) => {
  const spaceRegex = /[\s\p{Zs}]/gu;
  return Object.keys(data).reduce((acc, key) => {
    const value = data[key];
    if (typeof value === 'string') {
      acc[key] = value.replace(spaceRegex, ' ');
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});
};

// TODO: Cheesus, just create a Wizard component
export class _Signup extends Component {
  constructor(props) {
    super(props);
    const urlSearch = new URLSearchParams(props.location.search);
    const email = urlSearch.get('email');
    const promoCode = urlSearch.get('promoCode');

    this.state = {
      affiliate: affiliateGet(props.location.search),
      promoCode: promoCode,
      accountDetails: {
        data: {}
      },
      accountDetailFields: {
        email: {
          initial: email || '',
          required: true,
          // async forces debouncing TODO: Use valFunc(tors) like in Profile.js
          onChange: async (value) => props.onChangeEmail(value, this.props.partialUserRef)
        },
        password: {
          required: true,
          // async forces debouncing TODO: Use valFunc(tors) like in Profile.js
          onChange: async (value, fields) =>
            props.onChangePassword(value, fields, this.props.partialUserRef),
          error: passwordErrorMessage
        }
      },
      personalInfo: {
        data: {}
      },
      confirmation: {
        data: {
          promotionCode: promoCode || ''
        }
      },
      stepData: [
        {
          id: 'account-details',
          disabledContent: '1',
          fulfilledContent: '1',
          onClick: () => props.goto('account-details')
        },
        {
          id: 'personal-info',
          disabledContent: '2',
          fulfilledContent: '2',
          onClick: () => props.goto('personal-info')
        },
        {
          id: 'confirmation',
          disabledContent: '3',
          fulfilledContent: '3',
          onClick: () => props.goto('confirmation')
        }
      ],
      by: '',
      ref: '',
      complete: [],
      canAttemptVerification: false,
      canSendVerification: false,
      verified: false,
      isManualAddress: false,
      wizardErrorMessage: ''
    };
  }

  componentDidMount() {
    const {
      match: {
        params: { step }
      },
      location: { search },
      getRegistrationBanner,
      getPartialUserRef,
      partialUserRef
    } = this.props;
    const { affiliate } = this.state;
    const urlSearch = new URLSearchParams(search);
    affiliateSet(affiliate);
    this.mounted = true;
    this.validateStep(step);
    this.getParamOrLocal('by');
    this.getParamOrLocal('ref');
    getRegistrationBanner({
      affiliateId: affiliate?.affid,
      promoCode: urlSearch.get('promoCode'),
      by: urlSearch.get('by')
    });
    if (!partialUserRef) {
      getPartialUserRef({});
    }
    this.redirectReturningPlayerToLogin();
  }

  componentDidUpdate(prevProps) {
    const accountDetailsValue = Object.values(this.state.accountDetails.data).join('');
    if (accountDetailsValue) {
      addBeforeUnloadListener();
    } else {
      removeBeforeUnloadListener();
    }
    const {
      match: {
        params: { step: prevStep }
      }
    } = prevProps;
    const {
      match: {
        params: { step: currentStep }
      }
    } = this.props;

    if (prevStep !== currentStep) {
      if (window.scrollY > scrollTolerance) mover.move(document.body);
      this.validateStep(currentStep);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    removeBeforeUnloadListener();
    this.props.currentUser();
  }

  getParamOrLocal = (name) => {
    const {
      location: { search }
    } = this.props;
    const urlSearch = new URLSearchParams(search);
    let searchParam = '';
    if (urlSearch.has(name) && urlSearch.getAll(name).length === 1) {
      searchParam = urlSearch.get(name);
      this.setState({
        [name]: searchParam
      });
    }
    globalStore.getItem(name).then((localValue) => {
      if (!searchParam && localValue) {
        this.setState({
          [name]: localValue
        });
      } else if (searchParam && !localValue) {
        globalStore.setItem(name, searchParam);
      }
    });
  };

  setSlice = (data, slice) => {
    if (this.mounted) {
      this.setState((prevState) => ({
        ...prevState,
        [slice]: update(prevState[slice], {
          data: { $merge: data }
        }),
        accountDetailFields:
          slice === 'accountDetails'
            ? update(
                prevState.accountDetailFields,
                Object.keys(data).reduce((acc, cur) => {
                  // eslint-disable-next-line no-param-reassign -- reduce accumulator
                  acc[cur] = { initial: { $set: data[cur] } };
                  return acc;
                }, {})
              )
            : prevState.accountDetailFields
      }));
    }
  };

  setComplete = (formName, callback) => {
    const completeState = this.state.complete;
    if (this.mounted && !completeState.includes(formName)) {
      this.setState({ complete: [...completeState, formName] }, callback);
    } else {
      callback();
    }
  };

  setPartialComplete = (formName, field, value) => {
    this.setState((state) =>
      update(state, {
        [camelCase(formName)]: {
          data: {
            [field]: { $set: value }
          }
        }
      })
    );
  };

  setIncomplete = (formName, field, value) => {
    const { complete: completeState } = this.state;
    if (this.mounted && completeState.includes(formName)) {
      this.setState({ complete: completeState.filter((item) => item !== formName) });
    }
    this.setPartialComplete(formName, field, value);
  };

  setAccountDetails = async (data) => {
    this.setSlice(data, 'accountDetails');
    const { by } = this.state;
    const { partialRegister, goto, partialUserRef: ref } = this.props;

    this.setComplete('accountDetails', () => goto('personal-info'));
    return partialRegister({ ...data, by, ref });
  };

  setPersonalInfo = async (data) => {
    const sanitizedData = sanitizePersonalInfoData(data);
    const phoneWithPrefix = `${phonePrefix.UK}${sanitizedData.phoneNumber}`;
    this.setSlice(sanitizedData, 'personalInfo');
    const { by, accountDetails } = this.state;
    const { partialRegister, goto, partialUserRef: ref } = this.props;

    this.setComplete('personalInfo', () => goto('confirmation'));
    return partialRegister({
      ...sanitizedData,
      ...(sanitizedData.phoneNumber && { phoneNumber: phoneWithPrefix }),
      by,
      ref,
      ...accountDetails.data
    });
  };

  setConfirmation = async (data) => {
    this.setSlice(data, 'confirmation');
    const { affiliate, by, ref, accountDetails, personalInfo, verified, promoCode } = this.state;
    const phoneWithPrefix = `${phonePrefix.UK}${personalInfo.data.phoneNumber}`;
    const { partialUserRef, register, openAddPayment } = this.props;

    const result = await register(
      {
        ...data,
        by,
        ref,
        affiliate,
        partialUserRef,
        ...accountDetails.data,
        ...personalInfo.data,
        ...(personalInfo.data.phoneNumber && { phoneNumber: phoneWithPrefix }),
        ...(!shouldShowSignupPromoCode && { promotionCode: promoCode })
      },
      verified
    );

    if (result) {
      if (shouldShowMarketingPreferencesRedesign && data.marketingPreferences?.verticals) {
        trackMarketingPreferences('signup', data.marketingPreferences?.verticals);
      }
      localStore.reset();
      openAddPayment();
    }
    return result;
  };

  getPersonalInfoFields = () => {
    const {
      firstName,
      title,
      lastName,
      birthday,
      phoneNumber,
      postal,
      query,
      homeAddress,
      address,
      address2,
      city
    } = this.state.personalInfo.data;
    if (!this.personalInfoFields) {
      this.personalInfoFields = {
        firstName: {
          required: true,
          initial: firstName,
          onChange: firstOrLastNameValidation,
          error: 'Please enter your first name'
        },
        title: {
          required: true,
          initial: title || 'Miss'
        },
        lastName: {
          required: true,
          initial: lastName,
          onChange: firstOrLastNameValidation,
          error: 'Please enter your last name'
        },
        birthday: {
          required: true,
          initial: birthday,
          onChange: birthdayOver18,
          error: 'You must be over 18 to register'
        },
        phoneNumber: {
          required: __ENV__.MRQ_GAMING_AUTHORITY === 'MGA',
          initial: phoneNumber,
          onChange: validatePhone,
          error: phoneNumberErrorMessage,
          format: numbersOnly
        },
        homeAddress: {
          initial: homeAddress,
          required: true
        },
        address: {
          initial: address,
          onChange: validateRequired(addressRegex),
          error: 'Please enter your current address'
        },
        address2: {
          initial: address2,
          error: 'Please enter your current address',
          onChange: validateAddressLine2
        },
        query: {
          initial: query
        },
        city: {
          initial: city,
          error: 'Please enter your current city',
          onChange: validateRequired(cityRegex)
        },
        country: {
          initial: country
        },
        postal: {
          initial: postal,
          error: postcodeErrorMessage,
          onChange: validateRequired(postcodeRegex)
        }
      };
      if (__ENV__.MRQ_GAMING_AUTHORITY === 'MGA') {
        this.personalInfoFields.verificationCode = {
          onChange: phoneCode,
          error: 'Verification code is 4 digits'
        };
      }
      return this.personalInfoFields;
    }
    updateInitials(this.personalInfoFields, this.state.personalInfo.data);
    return this.personalInfoFields;
  };

  getConfirmationFields = () => {
    const { promotionCode, agreeResponsible, agreeTerms, agreeMarketing } =
      this.state.confirmation.data;
    if (!this.confirmationFields) {
      this.confirmationFields = {
        promotionCode: {
          initial: promotionCode,
          onChange: validatePromotionCode,
          required: false,
          error: "This doesn't look like a valid code"
        },
        agreeMarketing: {
          initial: isNil(agreeMarketing) ? true : agreeMarketing
        },
        agreeResponsible: {
          initial: agreeResponsible,
          required: true,
          error: 'You must agree with the Safer Gambling Policy to register',
          onChange: (value) => value === true // TODO: In Reform, make checkboxes have this
          // automatically
        },
        agreeTerms: {
          initial: agreeTerms,
          required: true,
          error: 'You must agree with the T&Cs to register',
          onChange: (value) => value === true
        }
      };
      return this.confirmationFields;
    }
    updateInitials(this.confirmationFields, this.state.confirmation.data);
    return this.confirmationFields;
  };

  /**
   * User is using the manual address input form
   *
   * @param isManualAddress {boolean}
   * */
  setAddressIsManual = (isManualAddress) => {
    this.personalInfoFields = update(this.personalInfoFields, {
      address: { required: { $set: isManualAddress } },
      postal: { required: { $set: isManualAddress } },
      city: { required: { $set: isManualAddress } }
    });
    this.setState({ isManualAddress });
    this.props.trackAddressInputModeChanged(isManualAddress);
  };

  handleAddressDropdownExpand = () => {
    this.props.trackAddressDropdownExpanded();
  };

  handleAddressDropdownOptionSelected = (addressType, addressValue) => {
    this.props.trackAddressDropdownOptionSelected(addressType, addressValue);
  };

  resetAddressErrors = () => {
    this.setState((prevState) =>
      update(prevState, {
        errors: { $unset: manualAddressFieldKeys }
      })
    );
  };

  handleVerify = async (data) => {
    const { partialUserRef, verify } = this.props;
    const result = await verify({
      ...data,
      ...this.state.accountDetails.data,
      partialUserRef
    });
    if (result.success && result.data.verified) {
      this.setState({ verified: true, canSendVerification: false });
    }
  };

  handleSendSms = async (data) => {
    const { partialUserRef, sendSms } = this.props;
    const result = await sendSms({
      ...data,
      ...this.state.accountDetails.data,
      partialUserRef
    });
    if (result.success && result.data.canSendVerification) {
      this.setState({ canSendVerification: true });
    }
  };

  handlePhone = async (data, event, currentTarget) => {
    if (currentTarget.name === 'verificationCode') {
      return this.handleVerify(data);
    } else if (currentTarget.name === 'phoneNumber') {
      return this.handleSendSms(data);
    }

    return null;
  };

  retryVerification = () => {
    this.setState({ canSendVerification: false });
  };

  updateWizardErrorMessage = (wizardErrorMessage) => {
    this.setState({ wizardErrorMessage });
  };

  validateStep = (step) => {
    if (step !== 'account-details' && step !== '') {
      this.redirectToPrevious(step);
    }
  };

  redirectToFirstError = (key) => {
    if (!key) return;
    const { gotoReplace } = this.props;

    if (Object.keys(this.state.accountDetailFields).includes(key)) {
      gotoReplace('account-details');
      return;
    }

    if (Object.keys(this.getPersonalInfoFields()).includes(key)) {
      gotoReplace('personal-info');
    }
  };

  redirectToPrevious = (current) => {
    const { complete } = this.state;
    const { gotoReplace } = this.props;
    switch (current) {
      case 'personal-info':
        if (!complete.includes('accountDetails')) {
          gotoReplace('account-details');
        }
        break;
      case 'confirmation':
        if (!complete.includes('accountDetails')) {
          gotoReplace('account-details');
        } else if (!complete.includes('personalInfo')) {
          gotoReplace('personal-info');
        }
        break;
      case 'account-details':
        break;
      default:
        gotoReplace('');
        break;
    }
  };

  redirectReturningPlayerToLogin = () => {
    if (isRedirectReturningPlayersEnabled && getReturningPlayer()) {
      this.props.goto('/login');
    }
  };

  handleError = ({ errors }) => {
    if (isEmpty(errors)) return;
    const errorMap = errors.reduce(toErrorMap, {});
    const firstKey = Object.keys(errorMap)[0];
    if (firstKey) this.redirectToFirstError(firstKey);
    this.setState({ errors: errorMap });
  };

  handleSuccess = async (request) => {
    await request;
    const { affiliate } = this.state;
    this.props.trackRegistrationSuccessEvent(affiliate);
    affiliateRemove();
  };

  onAccountDetailsSuccess = () => {
    this.props.trackRegistrationStartedEvent(this.state.affiliate);
    this.clearAccountDetailsErrors();
  };

  clearAccountDetailsErrors = () => {
    this.setState((prevState) =>
      update(prevState, {
        errors: { $unset: Object.keys(prevState.accountDetailFields) }
      })
    );
  };

  clearPersonalInfoErrors = () => {
    this.setState((prevState) =>
      update(prevState, {
        errors: { $unset: Object.keys(this.getPersonalInfoFields()) }
      })
    );
  };

  render() {
    const {
      match: {
        params: { step }
      },
      loading,
      exposeExperiment,
      registrationBanner,
      registerError
    } = this.props;
    const stepId = signupSteps.indexOf(step);
    const { promoCode, affiliate } = this.state;
    const affiliateId = affiliate?.affid;

    if (step === 'confirmation') {
      exposeExperiment(marketingPreferencesExperiment);
    }

    // const transform = `translateX(-${cc([
    //   {
    //     '0': step === 'account-details',
    //     '100': step === 'personal-info',
    //     '200': step === 'confirmation'
    //   }
    // ])}%)`;

    return (
      <div className="overflow-wrap">
        <Helmet>
          <title>{meta.title}</title>
          <meta name="description" content={meta.description} />
        </Helmet>
        <div className="signup__announcements">
          <Announcements />
        </div>
        <div
          className={cc([
            'signup__container',
            {
              'signup__container--accessDenied': registerError
            }
          ])}
        >
          {registerError ? (
            <AccessDenied
              className="userBox"
              route="signup"
              email={registerError.email}
              prompt={registerError.prompt}
              emailMasked={registerError.emailMasked}
            />
          ) : (
            <>
              <div className="signup__form">
                <UserBox
                  title="Sign up for MrQ"
                  id="signupBox"
                  className="userBox--white signupBox auth-general"
                >
                  {step ? (
                    <ProgressStage
                      className="larger"
                      stepId={step}
                      stepsData={this.state.stepData}
                    />
                  ) : null}
                  <MagicMove childKey={step} className="signupCarousel">
                    <div className="movable">
                      {step === 'account-details' ? (
                        <AccountDetails
                          onChange={this.setIncomplete}
                          fields={this.state.accountDetailFields}
                          submit={this.setAccountDetails}
                          refName="accountDetails"
                          validateOnPropChange
                          errors={this.state.errors}
                          onSuccess={this.onAccountDetailsSuccess}
                        />
                      ) : null}
                      {step === 'personal-info' ? (
                        <PersonalInfo
                          onChange={this.setIncomplete}
                          fields={this.getPersonalInfoFields()}
                          submit={this.setPersonalInfo}
                          extra={this.handlePhone}
                          updateWizardErrorMessage={this.updateWizardErrorMessage}
                          retryVerification={this.retryVerification}
                          accountDetails={this.state.accountDetails.data}
                          canAttemptVerification={this.state.canAttemptVerification}
                          canSendVerification={this.state.canSendVerification}
                          verified={this.state.verified}
                          refName="personalInfo"
                          validateOnPropChange
                          errors={this.state.errors}
                          setAddressIsManual={this.setAddressIsManual}
                          onAddressExpanded={this.handleAddressDropdownExpand}
                          onAddressOptionSelected={this.handleAddressDropdownOptionSelected}
                          isManualAddress={this.state.isManualAddress}
                          resetAddressErrors={this.resetAddressErrors}
                          onSuccess={this.clearPersonalInfoErrors}
                          exposeExperiment={exposeExperiment}
                        />
                      ) : null}
                      {step === 'confirmation' ? (
                        shouldShowMarketingPreferencesRedesign ? (
                          <Confirmation
                            refName="confirmation"
                            submit={this.setConfirmation}
                            onSuccess={this.handleSuccess}
                            onError={this.handleError}
                          />
                        ) : (
                          <ConfirmationOld
                            onChange={this.setIncomplete}
                            fields={this.getConfirmationFields()}
                            submit={this.setConfirmation}
                            wizardErrorMessage={this.state.wizardErrorMessage}
                            loading={loading}
                            refName="confirmation"
                            validateOnPropChange
                            errors={this.state.errors}
                            onSuccess={this.handleSuccess}
                            onError={this.handleError}
                          />
                        )
                      ) : null}
                    </div>
                  </MagicMove>
                </UserBox>
                {isMobile && (
                  <div className="userBox signup__offerPromo">
                    <RegistrationOfferPromoBanner promoCode={promoCode} affiliateId={affiliateId} />
                  </div>
                )}
                {step === 'account-details' ? <RegisterTrustFactors /> : null}
              </div>
              <SignupVerticalBanner banners={registrationBanner} step={stepId} />
            </>
          )}
        </div>
      </div>
    );
  }
}

_Signup.propTypes = {
  currentUser: PropTypes.func,
  onChangeEmail: PropTypes.func,
  registerError: PropTypes.string,
  goto: PropTypes.func,
  gotoReplace: PropTypes.func,
  openAddPayment: PropTypes.func,
  verify: PropTypes.func,
  sendSms: PropTypes.func,
  register: PropTypes.func,
  partialRegister: PropTypes.func,
  trackRegistrationStartedEvent: PropTypes.func,
  trackRegistrationSuccessEvent: PropTypes.func,
  trackAddressInputModeChanged: PropTypes.func,
  trackAddressDropdownExpanded: PropTypes.func,
  trackAddressDropdownOptionSelected: PropTypes.func,
  registrationBanner: PropTypes.array,
  partialUserRef: PropTypes.string,
  getRegistrationBanner: PropTypes.func
};

_Signup.defaultProps = {
  currentUser: null,
  onChangeEmail: null,
  partialUserRef: null,
  registerError: null,
  goto: null,
  gotoReplace: null,
  verify: null,
  sendSms: null,
  openAddPayment: null,
  register: null,
  partialRegister: null,
  trackRegistrationStartedEvent: null,
  trackRegistrationSuccessEvent: null,
  trackAddressInputModeChanged: null,
  trackAddressDropdownExpanded: null,
  trackAddressDropdownOptionSelected: null,
  registrationBanner: null,
  getRegistrationBanner: null
};

const mapStateToProps = (state) => ({
  partialUserRef: Auth.selectors.getPartialUserRef(state),
  loading: isLoading(state, [Auth.AT.FINGERPRINT._, Auth.AT.REGISTER._]),
  registrationBanner: Banner.selectors.model(state).registration,
  registerError: Auth.selectors.getRegisterError(state)
});

const mapDispatchToProps = (dispatch, { location: { search } }) => ({
  currentUser: () => Api.actions.user.getCurrent()(dispatch),
  getRegistrationBanner: (params) => {
    Api.actions.banner.registration(params)(dispatch, true);
  },
  onChangeEmail: validateEmail(dispatch),
  register: async ({
    agreeMarketing,
    marketingPreferences,
    affiliate,
    by,
    ref,
    partialUserRef,
    birthday,
    email,
    firstName,
    title,
    lastName,
    phoneNumber,
    promotionCode,
    password,
    username,
    homeAddress
  }) => {
    const { value: deviceInfo } = await dispatch(Auth.actions.getDeviceFingerprint());
    return Api.actions.auth.register(null, {
      homeAddress: homeAddress,
      affiliate: affiliate,
      birthDate: birthday,
      email: email,
      firstName: firstName,
      title: title,
      lastName: lastName,
      phoneNumber: phoneNumber,
      promotionCode: promotionCode,
      referrer: by,
      referralRef: ref,
      partialUserRef: partialUserRef,
      password: password,
      receiveCalls: agreeMarketing,
      receiveEmail: agreeMarketing,
      receivePost: agreeMarketing,
      receiveSms: agreeMarketing,
      marketingPreferences,
      username: username,
      deviceInfo: deviceInfo
    })(dispatch, true);
  },
  exposeExperiment: (experimentData) =>
    dispatch(Analytics.actions.exposeExperiment(experimentData)),
  openAddPayment: () => dispatch(Modals.actions.open('addPayment')),
  trackRegistrationStartedEvent: (affiliate) => {
    dispatch(Analytics.actions.registrationStarted(affiliate));
  },
  trackRegistrationSuccessEvent: (affiliate) => {
    dispatch(Analytics.actions.registrationSuccess(affiliate));
  },
  trackAddressInputModeChanged: (isManualAddress) => {
    dispatch(Analytics.actions.addressInputModeChanged({ isManualAddress }));
  },
  trackAddressDropdownExpanded: () => {
    dispatch(Analytics.actions.addressDropdownExpanded());
  },
  trackAddressDropdownOptionSelected: (addressType, addressValue) => {
    dispatch(Analytics.actions.addressDropdownOptionSelected({ addressType, addressValue }));
  },
  partialRegister: ({ ref, email, phoneNumber }) =>
    Api.actions.auth.partialRegister(null, {
      email,
      ref,
      phoneNumber
    })(dispatch, true),
  getPartialUserRef: () => {
    Api.actions.auth.partialUserRef()(dispatch, true);
  },
  verify: ({ partialUserRef, verificationCode }) =>
    Api.actions.auth.partialVerifySms(null, { partialUserRef, verificationCode })(dispatch, true),
  sendSms: ({ partialUserRef, email, phoneNumber }) =>
    Api.actions.auth.partialSendSmsVerification(null, {
      email,
      partialUserRef,
      phoneNumber
    })(dispatch, true),
  onChangePassword: (value, fields, partialUserRef) =>
    validatePassword(dispatch, { partialUserRef })(value, fields),
  stripSearch: (location) => dispatch(stripSearch(location)),
  goto: (route) => dispatch(push(`/signup/${route}${search}`)),
  gotoReplace: (route) => dispatch(replace(`/signup/${route}${search}`))
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLazyStyle(registrationMiddlestyle)
)(_Signup);
